import { useRef, type ReactNode } from 'react';
import { useNumberField, VisuallyHidden } from 'react-aria';
import { useNumberFieldState } from 'react-stately';
import { Minus, Plus } from '@hungryroot/icons';
import { Heading, IconButton } from '@hungryroot/ramen';

import styles from './NumberInput.module.css';

interface Props {
  label: string;
  value: number;
  displayValue?: string;
  infoCopy?: ReactNode;
  minValue?: number;
  maxValue?: number;
  className?: string;
  onChange: (value: number) => void;
  isLoading?: boolean;
  displayVariant?: DisplayVariant;
  maxDisplayValue?: string;
  buttonsClassName?: string;
  disabled?: boolean;
  visualMultiplier?: number;
}

export enum DisplayVariant {
  Oval = 'Oval',
  Circle = 'Circle',
}

const LETTER_WIDTH_REM = 0.6;

function calcInputWidth(value: string): number {
  const { length: strLength } = value;
  return strLength * LETTER_WIDTH_REM;
}

export function NumberInput({
  displayValue,
  className,
  buttonsClassName: buttonsClassNameProp,
  label,
  infoCopy,
  onChange,
  value,
  maxValue,
  minValue,
  isLoading,
  maxDisplayValue,
  displayVariant = DisplayVariant.Circle,
  disabled,
  visualMultiplier,
}: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const numberFieldState = useNumberFieldState({
    locale: 'en-US',
    minValue,
    maxValue,
    value,
    onChange,
    isDisabled: disabled || isLoading,
  });
  const {
    incrementButtonProps,
    decrementButtonProps,
    inputProps,
    labelProps,
    groupProps,
  } = useNumberField(
    {
      label,
      isDisabled: false,
      isReadOnly: true,
      onFocus: (e) => {
        Object.assign(e.target, {
          selectionEnd: 0,
        });
      },
      onKeyUp: (e) => {
        switch (e.key) {
          case 'ArrowUp': {
            numberFieldState.increment();
            break;
          }
          case 'ArrowDown': {
            numberFieldState.decrement();
          }
        }
      },
    },
    numberFieldState,
    inputRef
  );

  const valueStr = `${value}`;
  const inputDisplayValue =
    displayValue === undefined ? valueStr : displayValue;
  const inputWidthString =
    maxDisplayValue === undefined ? inputDisplayValue : maxDisplayValue;

  const inputWidth = calcInputWidth(inputWidthString);
  const inputElement = (
    <input
      {...inputProps}
      style={{ width: `${inputWidth}rem` }}
      ref={inputRef}
    />
  );

  const effectiveDisplayValue = visualMultiplier
    ? Number(inputDisplayValue) * visualMultiplier
    : inputDisplayValue;

  let buttonsClassName = `${styles.buttons} ${
    displayVariant === DisplayVariant.Oval ? styles['oval-container'] : ''
  }`;

  if (buttonsClassNameProp !== undefined) {
    buttonsClassName = `${buttonsClassName} ${buttonsClassNameProp}`;
  }
  return (
    <div className={className}>
      <div>
        <label {...labelProps}>
          <Heading variant="H6">{label}</Heading>
        </label>
        {infoCopy}
      </div>
      <div {...groupProps} className={buttonsClassName}>
        <IconButton
          className={
            displayVariant === DisplayVariant.Oval ? styles['button'] : ''
          }
          style={
            decrementButtonProps.isDisabled === true ? 'disabled' : 'muted'
          }
          onClick={() => {
            numberFieldState.decrement();
          }}
          size="small"
          prominence="secondary"
          icon={<Minus />}
          ariaLabel={decrementButtonProps['aria-label'] || ''}
        />
        <Heading variant="H6">
          <VisuallyHidden className={styles['input-container']}>
            {inputElement}
          </VisuallyHidden>
          <span
            style={{ width: `${inputWidth}rem` }}
            className={styles['display-value']}
            data-testid="number-input-display-value"
          >
            {effectiveDisplayValue}
          </span>
        </Heading>
        <IconButton
          className={
            displayVariant === DisplayVariant.Oval ? styles['button'] : ''
          }
          style={
            incrementButtonProps.isDisabled === true ? 'disabled' : 'muted'
          }
          onClick={() => {
            numberFieldState.increment();
          }}
          size="small"
          prominence="secondary"
          icon={<Plus />}
          ariaLabel={incrementButtonProps['aria-label'] || ''}
        />
      </div>
    </div>
  );
}
